// frontend/ReviewerDecMatrix.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Button, Form } from 'react-bootstrap';

const ReviewerDecMatrix = () => {
  const [platformID, setPlatformID] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [authorID, setAuthorID] = useState(null);
  const [submissionLevel, setSubmissionLevel] = useState('');
  const [matrixData, setMatrixData] = useState([]);
  const [selectedComment, setSelectedComment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const API = process.env.REACT_APP_API_BASE_URL;

  // Initialize platformID from localStorage
  useEffect(() => {
    const pid = parseInt(localStorage.getItem('platformID'));
    setPlatformID(pid);
  }, []);

  // Fetch all authors
  useEffect(() => {
    axios.get(`${API}/api/author/list`)
      .then(res => setAuthors(res.data))
      .catch(err => console.error("Author list error:", err));
  }, []);

  // Fetch matrix data when author and platform are selected
useEffect(() => {
  if (platformID && authorID && submissionLevel) {
    axios.get(`${API}/api/reviewer-decision/matrix`, {
      params: { platformID, authorID, submissionLevel }
    })
    .then(res => setMatrixData(res.data))
    .catch(err => console.error("Matrix API Error:", err));
  }
}, [platformID, authorID, submissionLevel]);


  // Handle comment click to fetch comment + reply details
  const handleCommentClick = async (submissionID, reviewerID, index) => {
    const res = await axios.get(`${API}/api/reviewer-decision/comment-detail`, {
      params: { submissionID, reviewerID, commentIndex: index }
    });
    setSelectedComment(res.data);
    setShowModal(true);
  };

  // Filter data based on submission level
  const filteredMatrixData = matrixData.filter(row => {
    if (!submissionLevel) return true;
    const level = row.SubmissionTitle?.toLowerCase() || '';
    if (submissionLevel === 'abstract') return /_abstract$/.test(level);
    if (submissionLevel === 'full_paper') return /_full_paper$/.test(level);
    if (submissionLevel === 'revision') return /_revision$/.test(level);
    return false;
  });

  return (
    <div className='container mt-4 ml-2'>
      <h3 className="page-title mb-4">Comment Matrix</h3>

      {/* Author Dropdown */}
      <Form.Group className="mb-3">
        <Form.Label>Select Author</Form.Label>
        <Form.Select
          value={authorID || ''}
          onChange={(e) => {
            const val = parseInt(e.target.value);
            setAuthorID(isNaN(val) ? null : val);
            setSubmissionLevel(''); // reset level when author changes
          }}
        >
          <option value="">-- Select Author --</option>
          {authors.map(author => (
            <option key={author.AuthorID} value={author.AuthorID}>
              {author.AuthorName}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {/* Submission Level Dropdown */}
      <Form.Group className="mb-3">
        <Form.Label>Select Submission Level</Form.Label>
        <Form.Select
          value={submissionLevel}
          onChange={(e) => setSubmissionLevel(e.target.value)}
          disabled={!authorID}
        >
          <option value="">-- Select Level --</option>
          <option value="abstract">Abstract</option>
          <option value="full_paper">Full Paper</option>
          <option value="revision">Revision</option>
        </Form.Select>
      </Form.Group>

      {/* Matrix Table */}
      <div className="table-responsive mt-3">
        {filteredMatrixData.length ? (
          <table className="table table-bordered text-center">
            <thead className="bg-primary text-light">
              <tr>
                <th>Submission</th>
                <th>Reviewer</th>
                <th colSpan={5}>Comments & Author Replies</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatrixData.map((row, index) => (
                <tr key={index}>
                  <td>{row.SubmissionTitle}</td>
                  <td>{row.ReviewerName ? `${row.ReviewerName.charAt(0)}*****${row.ReviewerName.slice(-1)}` : 'Reviewer'}</td>
                  {[1, 2, 3, 4, 5].map(n => (
                    <td
                      key={n}
                      style={{ cursor: n <= row.TotalComments ? 'pointer' : 'default' }}
                      onClick={() => n <= row.TotalComments && handleCommentClick(row.submissionID, row.ReviewerID, n)}
                    >
                      {n <= row.TotalComments ? n : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="alert alert-dark fw-bold text-center mb-3 rounded shadow-sm mt-3" role="alert">
            No comments on any submissions for this author
          </div>
        )}
      </div>

      {/* Comment + Reply Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Comments & Author Replies</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedComment ? (
            <div className="row">
              {/* Reviewer Comment */}
              <div className="col-md-6 mb-3">
                <div className="p-3 bg-light border-start border-4 border-warning shadow-sm rounded-start">
                  <h6 className="text-warning fw-bold">Comment</h6>
                  <p>{selectedComment.Comment || 'N/A'}</p>
                  <p><strong>Date:</strong> {selectedComment.ReviewDate ? new Date(selectedComment.ReviewDate).toLocaleString() : 'N/A'}</p>
                  <p><strong>Decision:</strong> {selectedComment.Decision === "AcceptWithComment" ? "Comment" : selectedComment.Decision || 'N/A'}</p>
                </div>
              </div>

              {/* Author Reply */}
              <div className="col-md-6 mb-3">
                <div className="p-3 bg-light border-start border-4 border-success shadow-sm rounded-start">
                  <h6 className="text-success fw-bold">Reply</h6>
                  <p>{selectedComment.AuthorReply || 'No reply yet'}</p>
                  <p><strong>Date:</strong> {selectedComment.AuthorReplayDate ? new Date(selectedComment.AuthorReplayDate).toLocaleString() : 'N/A'}</p>
                </div>
              </div>
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReviewerDecMatrix;
